import { useAsyncExecutor2 } from "async-executor-hook";
import { format } from "date-fns";
import { Paginator } from "primereact/paginator";
import { parse, stringify } from "query-string";
import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  from,
  interval,
  map,
  mapTo,
  startWith,
  Subject,
  switchMap,
  takeUntil,
} from "rxjs";

import { Authorization } from "../../common/AuthorizationCpt";
import { HTTP_METHODS } from "../../utils/constants";
import { getHHMM } from "../../utils/dates";
import { networkBackendRequest } from "../../utils/networkUtils";
import { ITEMS_PER_PAGE } from "../../utils/valueStaticConstants";
import PaymentLoader from "../PaymentLoader/PaymentLoader";
import StandaloneMessage from "../StandaloneMessage/StandaloneMessage";
import StatusTooltip from "../StatusTooltip/StatusTooltip";
import {
  DivColumStatus,
  PlateAmount,
  PlateBar,
  PlateSection,
  VctTicketRow,
} from "../VctTicket/VctTicketStyles";
import {
  TableHeader,
  VctTicketsContainer,
} from "../VctTickets/VctTicketsStyles";
import VctTicketSidebar from "../VctTicketSidebar/VctTicketSidebar";
import { VtcTicketConvertResponse } from "./CommonVtcTicket";
import { AppointmentConvert, IVtcTicketParams$ } from "./VctTickets.interface";
const TITLES = {
  agent: "AGENTE DE ADUANA",
  dispatcher: "ID/DESPACHADOR",
  date: "HORA DE RETIRO",
  plates: "PLACAS",
  segment: "SEGMENTO",
  dockNum: "COD. DIQUE",
  turnColumn: "TURNO",
  status: "ESTADO DE TURNO",
  actions: "ACCIONES",
  statusQuotes: "ESTADO DE CITAS",
};
const POLLING_INTERVAL = 10000;

const VtcTicketTable = (props: IVtcTicketParams$) => {
  const { params$ } = props;

  const destroy$Ref = useRef(new Subject());
  const destroy$: any = destroy$Ref.current;
  useEffect(() => {
    return () => {
      destroy$.next();
      destroy$.complete();
    };
  }, [destroy$]);

  const loadAdminAppointmentsTrack = useAsyncExecutor2(
    (params) => {
      const destroy$ = destroy$Ref.current;
      return interval(POLLING_INTERVAL).pipe(
        takeUntil(destroy$),
        startWith(params),
        mapTo(params),
        switchMap((params) => {
          return interval(POLLING_INTERVAL).pipe(
            takeUntil(destroy$),
            startWith(params),
            mapTo(params),
            switchMap((params) => {
              const request = networkBackendRequest(
                `/appointments/admin-track/?${stringify(params)}`,
                HTTP_METHODS.GET,
                undefined,
                undefined,
                undefined
              )
                .then((resp) => resp.json())
                .then(VtcTicketConvertResponse);
              return from(request);
            })
          );
        })
      );
    },
    {
      params$: params$.pipe(
        takeUntil(destroy$Ref.current),
        map((params) => ({
          ...params,
          withdrawal_from_date: format(
            params.withdrawal_from_date,
            "yyyy-MM-dd"
          ),
          withdrawal_to_date: format(params.withdrawal_to_date, "yyyy-MM-dd"),
          order: "descending",
          page: params.page ?? 1,
          page_size: ITEMS_PER_PAGE,
        }))
      ),
    }
  );
  const gridTemplateColumns = "25rem 1fr 1fr 1.5fr 1.5fr 1fr 1fr 1fr";
  const history = useHistory();

  const firstPlateFunction = (appointment: AppointmentConvert) => {
    const vehiclePlate = appointment.vehicles.map((vehicle) => vehicle.plate);
    const validateCita = vehiclePlate.length === 0 ? "-" : vehiclePlate[0];
    return validateCita;
  };
  const additionalPlatesFunction = (appointment: AppointmentConvert) => {
    const vehiclePlateadditional = appointment.vehicles.map(
      (vehicle) => vehicle.plate
    );
    const firstPlateAdditional =
      vehiclePlateadditional.length === 0 ? "-" : vehiclePlateadditional[0];

    const additionalPlates = vehiclePlateadditional
      .filter((plate) => {
        return plate !== firstPlateAdditional;
      })
      .map((plate) => {
        return {
          category: "neutral",
          label: plate,
        };
      });

    return additionalPlates;
  };
  const [showSidebar, updateShowSidebar] = useState<boolean>(false);
  const toggleSidebar = () => {
    updateShowSidebar(!showSidebar);
  };
  const [dataAppointment, setDataAppointment] = useState<AppointmentConvert>();
  return (
    <div>
      <Authorization actions={["appointment.get_appointment_admin_track"]}>
        {loadAdminAppointmentsTrack.status === "PROCESSING" && (
          <div style={{ marginRight: "2rem", marginLeft: "2rem" }}>
            <PaymentLoader numItems={ITEMS_PER_PAGE} />
          </div>
        )}
        <VctTicketsContainer gridTemplateColumns={gridTemplateColumns}>
          {loadAdminAppointmentsTrack.isSuccess &&
            loadAdminAppointmentsTrack.data.tickets.length === 0 && (
              <StandaloneMessage>No se encontraron citas.</StandaloneMessage>
            )}
          <>
            {loadAdminAppointmentsTrack.isSuccess &&
              loadAdminAppointmentsTrack.data.tickets.length > 0 && (
                <TableHeader className="item-container">
                  <div className="left-aligned-header">{TITLES.dispatcher}</div>
                  <div>{TITLES.date}</div>
                  <div>{TITLES.plates}</div>
                  <div>{TITLES.statusQuotes}</div>
                  <div>{TITLES.status}</div>
                  <div># TURNO</div>
                  <div>{TITLES.dockNum}</div>
                </TableHeader>
              )}

            {loadAdminAppointmentsTrack.isSuccess &&
              loadAdminAppointmentsTrack.data.tickets.map((appointment) => {
                return (
                  <VctTicketRow
                    onClick={() => {
                      toggleSidebar();
                      setDataAppointment(appointment);
                    }}
                    className="item-container"
                    key={appointment.id}
                  >
                    <div className="attribute dispatcher-info">
                      <div className="titles">{TITLES.dispatcher}</div>
                      <div className="dispatcher-info">
                        <p className="dispatcher-id">
                          {appointment.dispatcherDocumentNumber}
                        </p>
                        <p>{appointment.dispatcherName}</p>
                        <p className="volantes-num">
                          {`${appointment.volantes.length} volante${
                            appointment.volantes.length === 1 ? "" : "s"
                          }`}
                        </p>
                      </div>
                    </div>
                    <div className="attribute">
                      <div className="titles">{TITLES.date}</div>
                      <div className="aux-div">
                        <div style={{ display: "flex" }}>
                          <div style={{ marginRight: "0.2rem" }}>
                            {getHHMM(appointment.timeslot.start)}
                          </div>
                          <div>A</div>
                          <div style={{ marginLeft: "0.2rem" }}>
                            {getHHMM(appointment.timeslot.end)}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="attribute">
                      <div className="titles">{TITLES.plates}</div>
                      <div>
                        <PlateSection>
                          <PlateBar>
                            <span>
                              {firstPlateFunction(appointment).toUpperCase()}
                            </span>
                            {additionalPlatesFunction(appointment).length >
                            0 ? (
                              <PlateAmount status={"neutral"}>
                                <span>
                                  {"+" +
                                    additionalPlatesFunction(appointment)
                                      .length}
                                </span>
                              </PlateAmount>
                            ) : null}
                          </PlateBar>

                          {appointment.plates.length > 1 ? (
                            <StatusTooltip
                              className="tooltip"
                              statusList={appointment.plates
                                .filter(
                                  (plate) => plate !== appointment.plates[0]
                                )
                                .map((plate) => {
                                  return {
                                    category: "neutral",
                                    label: plate,
                                  };
                                })}
                            />
                          ) : null}
                        </PlateSection>
                      </div>
                    </div>
                    <div className="attribute">
                      <div className="titles">{TITLES.statusQuotes}</div>
                      <DivColumStatus status={appointment.statusQuote.category}>
                        <div className="type-column-hermes">
                          <b>{appointment.statusQuote.label.toUpperCase()}</b>
                        </div>
                      </DivColumStatus>
                    </div>
                    <div
                      className="attribute"
                      style={{ paddingLeft: "0.5rem" }}
                    >
                      <div className="titles">{TITLES.status}</div>
                      <DivColumStatus
                        status={appointment.appointmentStatus.category}
                      >
                        <div className="type-column-hermes">
                          <b>
                            {appointment.appointmentStatus.label.toUpperCase()}
                          </b>
                        </div>
                      </DivColumStatus>
                    </div>
                    <div className="attribute">
                      <div className="titles"># TURNO</div>
                      <div>{appointment.turnNum || "-"}</div>
                    </div>
                    <div className="attribute">
                      <div className="titles">{TITLES.dockNum}</div>
                      <div>{appointment.dockNum || "-"}</div>
                    </div>

                    <div className="attribute">
                      <div className="titles"></div>

                      {appointment.statusTurn && (
                        <div style={{ fontFamily: "Montserrat" }}>
                          <div>
                            {`${
                              appointment.diffInMinutes === 1
                                ? "Queda"
                                : "Quedan"
                            }`}
                          </div>

                          <div
                            style={{
                              color: `${
                                appointment.diffInMinutes >
                                appointment.redCountDownMinutes
                                  ? "#FEDA46"
                                  : "#FF6F61"
                              }`,
                              fontSize: "2.5rem",
                              fontWeight: 800,
                            }}
                          >
                            {appointment.diffInMinutes}
                          </div>
                          <div>
                            {`${
                              appointment.diffInMinutes === 1
                                ? "Minuto"
                                : "Minutos"
                            }`}
                          </div>
                        </div>
                      )}
                    </div>
                  </VctTicketRow>
                );
              })}
          </>
          {loadAdminAppointmentsTrack.status === "SUCCESS" &&
            loadAdminAppointmentsTrack.data.tickets.length > 0 && (
              <div className="flex-paginator">
                <Paginator
                  first={
                    (loadAdminAppointmentsTrack.data.page - 1) *
                    loadAdminAppointmentsTrack.data.page_size
                  }
                  rows={loadAdminAppointmentsTrack.data.page_size}
                  totalRecords={loadAdminAppointmentsTrack.data.count}
                  onPageChange={(e) => {
                    const targetPage =
                      e.first / loadAdminAppointmentsTrack.data.page_size;
                    const pathname = history.location.pathname;
                    const parsedParams = parse(history.location.search);
                    const stringifiedParams = stringify({
                      ...parsedParams,
                      page: targetPage + 1,
                    });
                    history.push(`${pathname}?${stringifiedParams}`);
                  }}
                />
              </div>
            )}
          {showSidebar && (
            <VctTicketSidebar
              onOutsideClick={() => {
                toggleSidebar();
              }}
              appointment={dataAppointment}
            />
          )}
        </VctTicketsContainer>
      </Authorization>
    </div>
  );
};

export default VtcTicketTable;
